import { useState } from "react"
import {Search, Loader2, ShieldCheck, AlertCircle} from 'lucide-react'

const PROJECT_ID = "p427286551862546432"
const FUNCTION_URL = `https://api.lumi.new/v1/functions/${PROJECT_ID}/parking-appeals`

interface StatusResult {
  appealReference: string
  status: string
  noticeNumber: string
  submittedDate: string
}

function AppealStatusLookup() {
  const [open, setOpen] = useState(false)
  const [reference, setReference] = useState("")
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<StatusResult | null>(null)
  const [error, setError] = useState("")

  const statusStep = (status: string) => {
    if (["Approved", "Partially Approved", "Denied", "Closed"].includes(status)) return 3
    if (["Under Review", "Additional Information Required"].includes(status)) return 2
    return 1
  }

  const handleCheck = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")
    setResult(null)

    try {
      const response = await fetch(FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "status",
          appealReference: reference.trim(),
          email: email.trim(),
        }),
      })

      const data = await response.json()

      if (!response.ok || !data?.found) {
        setError("We could not find an appeal matching that reference number and email address.")
        return
      }

      setResult(data.appeal)
    } catch (err) {
      console.error("Appeal status lookup failed:", err)
      setError("Something went wrong while checking your appeal status. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Already submitted an appeal?</h3>
          <p className="mt-1 text-sm text-slate-500">Check the status of an existing appeal.</p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex-shrink-0 flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-slate-700"
        >
          <Search className="h-4 w-4" />
          Check Appeal Status
        </button>
      </div>

      {open ? (
        <form onSubmit={handleCheck} className="mt-6 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">Appeal Reference Number</label>
            <input
              type="text"
              required
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="SEN-APL-000123"
              className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/40"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">Email Address</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/40"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="sm:col-span-2 flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-6 py-3 font-semibold text-white transition-all hover:bg-sky-500 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {loading ? "Checking..." : "Check Status"}
          </button>
        </form>
      ) : null}

      {error ? (
        <div className="mt-5 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      ) : null}

      {result ? (
        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <ShieldCheck className="h-4 w-4 text-sky-600" />
            Appeal {result.appealReference}
          </div>
          <div className="mt-4 flex items-center justify-between text-xs font-semibold text-slate-500">
            <span className={statusStep(result.status) >= 1 ? "text-sky-600" : ""}>Submitted</span>
            <span className={statusStep(result.status) >= 2 ? "text-sky-600" : ""}>Under Review</span>
            <span className={statusStep(result.status) >= 3 ? "text-sky-600" : ""}>Decision Made</span>
          </div>
          <div className="mt-2 h-1.5 w-full rounded-full bg-slate-200">
            <div
              className="h-1.5 rounded-full bg-sky-500 transition-all"
              style={{ width: `${(statusStep(result.status) / 3) * 100}%` }}
            />
          </div>
          <p className="mt-4 text-sm text-slate-600">
            Current status: <span className="font-semibold text-slate-900">{result.status}</span>
          </p>
        </div>
      ) : null}
    </div>
  )
}

export default AppealStatusLookup
